/**
 * Splits an office title such as
 *   "Brickell City Centre, 78 SW 7th Street, Miami, Florida, USA"
 * into a bold primary line ("Brickell City Centre") and a lighter secondary
 * line ("78 SW 7th Street · Miami") for the enquiry dialog header.
 *
 * Titles come from the Instant feed and are not consistent: some lead with a
 * floor or suite, some with a bare house number, some include the city and
 * region, some neither. The rules here favour showing something sensible for
 * every shape rather than being clever for any one of them.
 */

// A whole part that is only a floor, suite, unit, tower or block reference
const FLOOR_WORDS = 'floors?|level|levels|lvl|suites?|ste\\.?|units?|rooms?|sala|piso|pisos|andar|kat|lantai|oficina|ofis|étage|etage|stock|og|podium|mezzanine|basement|penthouse|leval';
const NUMBER_THEN_FLOOR = new RegExp(`^\\d+[a-z]?(\\s*[-&/]\\s*\\d+[a-z]?)?\\s+(${FLOOR_WORDS})\\b`, 'i');
const FLOOR_LEAD = new RegExp(`^(ground|lower|upper|top|first|second|third|fourth|fifth|\\d+(st|nd|rd|th)?[a-z]?)\\s*(&|and|-|/)?\\s*(\\d+(st|nd|rd|th)?)?\\s*(${FLOOR_WORDS})\\b`, 'i');
const FLOOR_WORD_LEAD = new RegExp(`^(${FLOOR_WORDS})\\b`, 'i');
// Asian style floor tokens: 6/F, 17F, 1F & 2F, 21F/29F, 3-4/F, 48/F
const FLOOR_TOKEN = /\d+(?:st|nd|rd|th)?(?:\s*[-&/]\s*\d+(?:st|nd|rd|th)?)?\s*\/?\s*F\b\.?/;
const FLOOR_TOKEN_ONLY = new RegExp(`^${FLOOR_TOKEN.source}$`);
const FLOOR_TOKEN_LEAD = new RegExp(`^${FLOOR_TOKEN.source}[\\s,]+`);
const FLOOR_TOKEN_TRAIL = new RegExp(`[\\s,]+${FLOOR_TOKEN.source}$`);
// Floor phrases inside a part: "West Tower 2nd Floor" -> "West Tower", "Top floor Safalya Apartment" -> "Safalya Apartment"
const FLOOR_PHRASE = '(ground|lower|upper|top|first|second|third|fourth|fifth|\\d+(st|nd|rd|th)?)\\s*(&|and|-|/)?\\s*(\\d+(st|nd|rd|th)?)?\\s*(floors?|level|fl\\.?)';
const FLOOR_PHRASE_LEAD = new RegExp(`^${FLOOR_PHRASE}\\b[\\s,]*`, 'i');
const FLOOR_PHRASE_TRAIL = new RegExp(`[\\s,]+${FLOOR_PHRASE}\\.?$`, 'i');
const STRUCTURE_ONLY = /^(tower|towers|building|bldg\.?|block|wing|phase|plot|plot no\.?|gate|torre|bloc|blok)\s*[-#:.]?\s*(no\.?\s*)?[a-z0-9]{1,4}(\s*(&|and)\s*[a-z0-9]{1,4})?\.?$/i;
const UNIT_NUMBER = /^((no\.?|no:|nr\.?|#)\s*)?[a-z]?\d+[a-z]?([-/]\d+[a-z]?)?$/i;
// "Office No. 12", "Oficina 1101", "Ofis 3"
const OFFICE_NUMBER = /^(office|oficina|ofis)\s*(no\.?|no:|#)?\s*[a-z]?\d+[a-z]?$/i;
// "40", "17", "6 & 7", "No. 1624", "No: 4", "Nr. 12" (not "#08-01" or "2601-05", which are units)
const BARE_NUMBER = /^(no\.?|no:|nr\.?|nº)?\s*\d+[a-z]?(\s*(&|\/)\s*\d+[a-z]?)?$/i;

const COUNTRY_ALIASES = [
    ['usa', 'us', 'united states', 'united states of america', 'u.s.a.', 'u.s.'],
    ['uk', 'united kingdom', 'great britain', 'england', 'scotland', 'wales', 'northern ireland', 'u.k.'],
    ['hong kong', 'hong kong sar', 'hk'],
    ['uae', 'united arab emirates', 'u.a.e.'],
];

const SEPARATOR = ' · ';
const MAX_LOCALITY_LENGTH = 34;
const MAX_SECONDARY_LENGTH = 64;
const MAX_PRIMARY_LENGTH = 40;

function normalise(value) {
    return String(value || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[‘’']/g, "'")
        .replace(/[-–]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function isFloorish(part) {
    // "Koenigsallee 2a (5th floor)": judge the part without its parenthetical
    const text = part.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();
    if (!text) {
        return true;
    }
    if (UNIT_NUMBER.test(text) || FLOOR_TOKEN_ONLY.test(text) || STRUCTURE_ONLY.test(text) || OFFICE_NUMBER.test(text)) {
        return true;
    }
    // Short parts only: "5455 unit 208 152nd Street" is an address, "Suite 200" is not
    const words = text.split(' ');
    if (words.length > 4) {
        return false;
    }
    if (FLOOR_LEAD.test(text) || FLOOR_WORD_LEAD.test(text)) {
        return true;
    }
    if (/^(upper |lower )?(ground|high|gf)\b.*\bfloor\b/i.test(text)) {
        return true;
    }
    // "250 Suite", "2 Andar": a number followed directly by a floor word
    return NUMBER_THEN_FLOOR.test(text);
}

function isBareNumber(part) {
    const text = part.trim();
    return BARE_NUMBER.test(text) && !FLOOR_TOKEN_ONLY.test(text);
}

// "17F Jing An Building" -> "Jing An Building", "Solala Plaza 3F" -> "Solala Plaza"
function stripFloorTokens(part) {
    const stripped = part
        .replace(FLOOR_TOKEN_LEAD, '')
        .replace(FLOOR_TOKEN_TRAIL, '')
        .replace(FLOOR_PHRASE_LEAD, '')
        .replace(FLOOR_PHRASE_TRAIL, '')
        .trim();
    // "37/F & 38/F" leaves only "&" behind: the whole part was floor information
    if (/^[\s&/+-]*(and)?[\s&/+-]*$/i.test(stripped)) {
        return FLOOR_TOKEN_ONLY.test(part.trim()) || stripped !== part.trim() ? '' : part;
    }
    return stripped || part;
}

function matchesCity(part, city) {
    const a = normalise(part);
    const b = normalise(city);
    if (!a || !b) {
        return false;
    }
    if (a === b) {
        return true;
    }
    // "New York" vs "New York City", "Ho Chi Minh" vs "Ho Chi Minh City"
    if (a.length >= 4 && b.startsWith(`${a} `)) {
        return true;
    }
    return a === `${b} city`;
}

function matchesCountry(part, country) {
    const a = normalise(part);
    const b = normalise(country);
    if (!a) {
        return false;
    }
    if (b && a === b) {
        return true;
    }
    const aliases = COUNTRY_ALIASES.find((list) => list.includes(b) || list.includes(a));
    return Boolean(aliases && aliases.includes(a));
}

/**
 * @param {string} title   Full office title from the entry
 * @param {string} city    City field
 * @param {string} country Country name field
 * @param {{ maxPrimary?: number, maxSecondary?: number }} options
 *   Length limits for tight layouts such as sidebar cards
 * @returns {{ primary: string, secondary: string, area: string } | null}
 *   null when there is nothing useful to show on a second line.
 *   `area` is the secondary line without the city or country, for cards that
 *   already show the city elsewhere; it may be empty.
 */
export function splitOfficeAddress(title, city = '', country = '', options = {}) {
    const maxPrimary = options.maxPrimary || MAX_PRIMARY_LENGTH;
    const maxSecondary = options.maxSecondary || MAX_SECONDARY_LENGTH;
    const rawParts = String(title || '')
        .split(',')
        .map((part) => stripFloorTokens(part.trim()))
        .filter(Boolean);

    if (!rawParts.length) {
        return null;
    }

    // Reattach bare house numbers to their street:
    //   "Rua Argolino de Morães, 87, ..." -> "Rua Argolino de Morães, 87"
    //   "40, Berkeley Square, ..."        -> "40 Berkeley Square"
    const parts = [];
    for (let index = 0; index < rawParts.length; index += 1) {
        const part = rawParts[index];
        const previous = parts[parts.length - 1];
        const next = rawParts[index + 1];
        if (isBareNumber(part) && previous && !/\d/.test(previous) && !isFloorish(previous)) {
            parts[parts.length - 1] = `${previous}, ${part}`;
        } else if (isBareNumber(part) && !previous && next && !isBareNumber(next) && !isFloorish(next)) {
            parts.push(`${part} ${next}`);
            index += 1;
        } else {
            parts.push(part);
        }
    }

    // Primary: the first part that is not a floor, suite, unit or tower reference.
    // Titles that lead with the city ("Prague, River Garden, ...") skip past it.
    const candidates = parts
        .map((part, index) => ({ part, index }))
        .filter(({ part }) => !isFloorish(part));
    const preferred = candidates.find(({ part }) => !matchesCity(part, city)) || candidates[0];
    const primaryIndex = preferred ? preferred.index : 0;
    const primaryBase = parts[primaryIndex];

    const rest = parts.slice(primaryIndex + 1);

    // Anything after the city in the title is region or country noise
    const cityIndex = rest.findIndex((part) => matchesCity(part, city));
    const localitySource = cityIndex >= 0 ? rest.slice(0, cityIndex) : rest;

    const localities = [];
    localitySource.forEach((part) => {
        if (isFloorish(part) || matchesCountry(part, country) || matchesCity(part, city)) {
            return;
        }
        if (part.length > MAX_LOCALITY_LENGTH || /[()]/.test(part)) {
            return;
        }
        if (normalise(part) === normalise(primaryBase)) {
            return;
        }
        if (localities.some((existing) => normalise(existing) === normalise(part))) {
            return;
        }
        localities.push(part);
    });

    // Balance the two lines: a short building name reads better with its street
    // beside it ("Albury Office Park, Magalieszicht Avenue" / "Hyde Park · Johannesburg")
    let primary = parts[primaryIndex];
    const remaining = localities.slice();
    while (remaining.length) {
        const candidate = `${primary}, ${remaining[0]}`;
        const secondaryGuess = [...remaining.slice(0, 2), city].filter(Boolean).join(SEPARATOR);
        const imbalance = secondaryGuess.length - primary.length;
        const shortPrimary = primary.length < 20;
        if (imbalance <= 8 || (!shortPrimary && imbalance <= 16) || candidate.length > maxPrimary) {
            break;
        }
        primary = candidate;
        remaining.shift();
    }

    const area = remaining.slice(0, 2).join(SEPARATOR);
    const secondaryParts = remaining.slice(0, 2);
    if (city && !matchesCity(primary, city)) {
        secondaryParts.push(city);
    }
    if (
        secondaryParts.length < 2
        && country
        && !matchesCountry(primary, country)
        && normalise(country) !== normalise(city)
    ) {
        secondaryParts.push(country);
    }

    // Keep the line readable: drop localities from the front until it fits
    while (secondaryParts.length > 1 && secondaryParts.join(SEPARATOR).length > maxSecondary) {
        secondaryParts.shift();
    }

    const secondary = secondaryParts.join(SEPARATOR);
    if (!secondary) {
        return null;
    }

    return { primary, secondary, area };
}
